import { CustomTheme, OnboardingDetailsRole } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { paymentMethodHasCompanyCifRequiredLender } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/utils/companyCifRequiredLender';
import { StoredBorrowerOnboardingDetails } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import { useMerchantPosContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/_shared/contexts/MerchantPosContext';
import { BorrowerOnboardingDetailsMap } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/maps/borrowerOnboardingDetails.map';
import { BorrowerOnboardingDetailsFormType } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/schemas/borrowerOnboardingDetailsForm.schema';
import { OnboardingDetailsRepositoryFactory } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/entities/OnboardingDetailsRepositoryFactory';

export const useMerchantPos = () => {
  const {
    agent,
    selectedRole,
    borrowerOnboardingDetails,
    checkoutSessionDetailEntity,
    paymentMethodDetailEntity,
    update,
  } = useMerchantPosContext();

  const currency = paymentMethodDetailEntity.checkoutSession.totalPrice.currency;

  const [merchantRepository] = useState(OnboardingDetailsRepositoryFactory.getRepository());

  const additionalProfessionalDetailsRequired = !!(
    agent &&
    agent.customTheme !== CustomTheme.CAIXABANK &&
    paymentMethodDetailEntity.isAuto()
  );

  const isCompanyCifRequired =
    additionalProfessionalDetailsRequired &&
    paymentMethodHasCompanyCifRequiredLender(paymentMethodDetailEntity.paymentMethod);

  const setSelectedRole = (role: OnboardingDetailsRole | undefined) => {
    update({ selectedRole: role });
  };

  const setBorrowerOnboardingDetails = (borrowerOnboardingDetails: StoredBorrowerOnboardingDetails[]) => {
    update({
      borrowerOnboardingDetails: merchantRepository.castDates(borrowerOnboardingDetails),
    });
  };

  const transformFormValues = (formValues: BorrowerOnboardingDetailsFormType): StoredBorrowerOnboardingDetails => {
    if (!selectedRole) {
      throw new Error('Selected role is required');
    }
    return BorrowerOnboardingDetailsMap.fromFormToStored(
      formValues,
      selectedRole,
      currency,
      agent.customTheme,
      paymentMethodDetailEntity.isAuto(),
    );
  };

  const addOrUpdateBorrowerOnboardingDetails = async (onboardingDetails: StoredBorrowerOnboardingDetails) => {
    const allOnboardingDetails = substituteOrAppend(borrowerOnboardingDetails, onboardingDetails, 'role');
    setBorrowerOnboardingDetails(allOnboardingDetails);
    merchantRepository.set(agent.id, allOnboardingDetails);
    setSelectedRole(undefined);
  };

  const deleteBorrowerOnboardingDetails = (role: OnboardingDetailsRole) => {
    setBorrowerOnboardingDetails(borrowerOnboardingDetails.filter((participant) => participant.role !== role));
  };

  const createApplications = async (): Promise<void> => {
    if (!borrowerOnboardingDetails.length) {
      throw new Error('Borrower onboarding details are required');
    }

    try {
      const clientSecret = await checkoutSessionDetailEntity.getClientSecret();
      await paymentMethodDetailEntity.createApplications(clientSecret, borrowerOnboardingDetails);
      //await paymentMethodDetailEntity.waitUntilSelectedApplication();
      //await paymentMethodDetailEntity.refresh();
      //await paymentMethodDetailEntity.waitUntilDocumentationRequiredFilesExist();
    } catch (error) {
      throw new Error(`Couldn't create loan applications: ${error}`); // to do: handle exception in error boundary
    } finally {
      //await refreshCheckoutSessionEntity([paymentMethodDetail]);
    }
  };

  return {
    selectedRole,
    setSelectedRole,
    transformFormValues,
    addOrUpdateBorrowerOnboardingDetails,
    deleteBorrowerOnboardingDetails,
    createApplications,
    additionalProfessionalDetailsRequired,
    isCompanyCifRequired,
    currency,
  };
};

function substituteOrAppend<T>(array: T[], newObj: T, key: keyof T): T[] {
  // Find index of object with matching key
  const index = array.findIndex((item) => item[key] === newObj[key]);

  if (index !== -1) {
    // If found, replace the existing object
    array[index] = newObj;
  } else {
    // If not found, append the new object
    array.push(newObj);
  }

  return array;
}
